import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { DatePipe, CurrencyPipe, DecimalPipe, SlicePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { GetMakeInvoicesService } from 'src/app/shared/services/api/get-make-invoices.service';
import { WalletSummaryService } from 'src/app/shared/services/api/wallet-summary.service';

@Component({
  selector: 'app-apply-rewards',
  standalone: true,
  imports: [DatePipe, CurrencyPipe, DecimalPipe, SlicePipe, FormsModule], 
  templateUrl: './apply-rewards.component.html',
  styleUrl: './apply-rewards.component.scss'
})
export class ApplyRewardsComponent implements OnInit {

  @Output() rewardApplied = new EventEmitter<void>();
  
  invoices: any[] = []; 
  isModalOpen: boolean = false;
  isSubmitted: boolean = false;
  selectedInvoice: any = null;

  walletID: string = '';
  walletRole: string = '';
  sessionID: string = '';

  //availablePoints: number = 20000;

  totalRewardsEarned: number = 0;
  totalRewardsBalance: number = 0;
  rewardsConversionFactor: number = 0;
  pointsUsdValue: number = 0;
  rewardsUsdValue: number = 0;

  enteredPoints: number | null = 0;

  appliedPointsSnapshot: number = 0;
  appliedUsdSnapshot: number = 0;
  redemptionDate: Date = new Date();

  isExpanded: boolean = false;
  readonly INITIAL_ROW_COUNT: number = 4;
  readonly MIN_REWARDS_POINTS: number = 7;

  // 1. Inject the invoice service via constructor
  constructor(
    private invoiceService: GetMakeInvoicesService,
    private walletSummaryService: WalletSummaryService
  ) {}

  get calculatedAvailablePoints(): number {
    const remaining = this.totalRewardsBalance - this.enteredPoints;
    return remaining < 0 ? 0 : remaining; // Prevent negative balances
  }

  get dynamicPointsUsdValue(): number {
    return (this.enteredPoints ?? 0) * this.rewardsConversionFactor;
  }

  ngOnInit(): void {

    this.initializeSessionContext();
  
    const activeWalletID = this.walletID || "W3000011571";
    const activeRole = this.walletRole || "admin";

    this.loadInvoiceData(activeWalletID, activeRole);
    this.loadWalletSummaryData(activeWalletID, activeRole);
  }

  private initializeSessionContext() {
    const rawWalletID = sessionStorage.getItem("WalletID") || '';
    const rawRole = sessionStorage.getItem("WalletRole") || '';
    const rawSessionID = sessionStorage.getItem("SessionID") || '';

    // Strip out literal double quotes safely
    this.walletID = rawWalletID.replace(/"/g, '').trim();
    this.walletRole = rawRole.replace(/"/g, '').trim();
    this.sessionID = rawSessionID.replace(/"/g, '').trim();

    console.log('🧼 Unified Session Context Initialized:', {
      walletID: this.walletID,
      walletRole: this.walletRole,
      sessionID: this.sessionID
    });
  }

  pendingRewardsMap: { [invoiceNumber: string]: { points: number; usd: number } } = {};

loadInvoiceData(walletID: string, role: string) {
    this.invoiceService.fetchInvoiceDetails(walletID, role).subscribe({
      next: (response) => {
        if (response?.upsdotcom?.data) {
          // Merge any previously stored local rewards into the fetched list
          this.invoices = response.upsdotcom.data.map((inv: any) => {
            const saved = this.pendingRewardsMap[inv.invoiceNumber];
            if (saved) {
              return {
                ...inv,
                walletInvoiceStatus: 'Pending',
                localAppliedPoints: saved.points,
                localAppliedUsd: saved.usd
              };
            }
            return inv;
          });

          this.invoices = this.invoices.sort((a: any, b: any) => {
            const dateA = new Date(a.invoiceDate).getTime();
            const dateB = new Date(b.invoiceDate).getTime();
            return dateA - dateB;
          });
          console.log('Invoices fetched and merged:', this.invoices);
        }
      },
      error: (err) => console.error('Invoices fetch failed:', err)
    });
  }

loadWalletSummaryData(walletID: string, role: string) {
    this.walletSummaryService.fetchWalletSummary(walletID, role).subscribe({
      next: (summary) => {
        console.log('================ WALLET SERVICE INTERCEPTOR WORKED ================');
        const wallet = summary?.wallet;
        const rewardsBlock = wallet?.rewards;

        if (rewardsBlock) {
          this.totalRewardsEarned = parseFloat(rewardsBlock.totalRewardsEarned || '0');
          this.totalRewardsBalance = parseFloat(rewardsBlock.totalRewardBalance || '0');
          this.rewardsConversionFactor = rewardsBlock.rewardsConversionFactor || 0;
          this.pointsUsdValue = this.totalRewardsEarned * this.rewardsConversionFactor;
          this.rewardsUsdValue = this.totalRewardsBalance * this.rewardsConversionFactor;
          
          console.log(`Success! Total Points: ${this.totalRewardsBalance} | Value: $${this.rewardsUsdValue}`);
        }
      },
      error: (err) => console.error('Wallet Summary Service failed:', err)
    });
  }

  onSubmitRewardPayment() {
    if (!this.selectedInvoice || this.isPointsInputInvalid) return;

    this.appliedPointsSnapshot = this.enteredPoints;
    this.appliedUsdSnapshot = this.dynamicPointsUsdValue;
    this.redemptionDate = new Date();

    // 1. Remember the value for this specific invoice
    const invoiceNum = this.selectedInvoice.invoiceNumber;
    this.pendingRewardsMap[invoiceNum] = {
      points: this.appliedPointsSnapshot,
      usd: this.appliedUsdSnapshot
    };

    // 2. Also update the currently selected invoice reference
    this.selectedInvoice.walletInvoiceStatus = 'Pending';
    this.selectedInvoice.localAppliedPoints = this.appliedPointsSnapshot;
    this.selectedInvoice.localAppliedUsd = this.appliedUsdSnapshot;

    // Reuses your clean centralized properties effortlessly!
const transactionPayload = {
      walletID: this.walletID || "W3000011571",
      sessionID: this.sessionID || "10c5636d-7fba-4cb6-9a64-927478b013c7",
      walletRole: this.walletRole || "admin",
      invoiceID: this.selectedInvoice.invoiceNumber,
      productSystem: "upsdotcom",
      sourceSystemID1: this.selectedInvoice.sourceSystemID1 || "",
      userID: "1234",
      inProgressRewards: this.enteredPoints.toString(),
      inProgressRewardsValue: this.dynamicPointsUsdValue.toFixed(2),
      inProgressCashValue: "0",
      paymentValue: this.selectedInvoice.invoiceAmount || "0",
      totalpaymentAmount: this.selectedInvoice.outStandingAmount || "0",
      currency: "USD",
      tokenReference: "",
      cvv: "",
      billingSystem: "upsbc",
      transactionID: ""
    };

    console.log('Submitting payload to DWMakeInvoicePayment API:', transactionPayload);

    this.invoiceService.submitInvoicePayment(transactionPayload).subscribe({
      next: (response) => {
        console.log('🎉 Reward Payment Successful:', response);
        this.isSubmitted = true; 
        this.loadInvoiceData(transactionPayload.walletID, transactionPayload.walletRole);
        this.loadWalletSummaryData(transactionPayload.walletID, transactionPayload.walletRole);
      },
      error: (err) => console.error('❌ Reward submission failed:', err)
    });
  }
  

  onApplyRewards(invoice: any) {
    this.selectedInvoice = invoice;

    this.isSubmitted = false;
    this.isModalOpen = true;

    this.enteredPoints = this.maxAllowedPointsForInvoice;
       // this.enteredPoints = 0;
  }

  onCloseModal() {
    this.isModalOpen = false;

    if (this.isSubmitted) {
      this.rewardApplied.emit();
    }

    this.isSubmitted = false;
    this.selectedInvoice = null;

    const activeWalletID = this.walletID || "W3000011571";
    const activeRole = this.walletRole || "admin";
    this.loadWalletSummaryData(activeWalletID, activeRole);
  }

  onGoToBillingCenter() {
    this.onCloseModal();
    window.location.href = 'https://billing.ups.com/home';
  }

  onSeeMore() {
    this.isExpanded = !this.isExpanded;
    console.log('Load more rows...');
  }

  get maxAllowedPointsForInvoice(): number {
  if (!this.selectedInvoice || !this.rewardsConversionFactor) {
    return Math.floor(this.totalRewardsBalance);
  }
  
  const outstandingAmount = parseFloat(this.selectedInvoice.outStandingAmount || '0');
  
  // Convert outstanding USD balance into equivalent points
  const pointsNeededForInvoice = this.rewardsConversionFactor > 0 
    ? Math.floor(outstandingAmount / this.rewardsConversionFactor) 
    : 0;

  // The maximum allowed is the smaller of: Available Points OR Points needed to pay the outstanding balance
  return Math.min(this.totalRewardsBalance, pointsNeededForInvoice);
}

get isPointsInputInvalid(): boolean {
  if (this.enteredPoints === null || this.enteredPoints === undefined || isNaN(this.enteredPoints)) {
    return true;
  }
  if (this.enteredPoints < this.MIN_REWARDS_POINTS) {
    return true;
  }
  if (this.enteredPoints > this.totalRewardsBalance) {
    return true;
  }
  if (this.enteredPoints > this.maxAllowedPointsForInvoice) {
    return true;
  }
  return false;
}

get pointsErrorMessage(): string {
 if (this.enteredPoints === null || this.enteredPoints === undefined || isNaN(this.enteredPoints) || this.enteredPoints < this.MIN_REWARDS_POINTS) {
      return `A minimum of ${this.MIN_REWARDS_POINTS} points ($0.01) is required to apply Rewards points to your invoice.`;
    }
  if (this.enteredPoints > this.totalRewardsBalance) {
    return `You cannot enter more than your available points (${this.totalRewardsBalance.toLocaleString()} pts).`;
  }
  if (this.enteredPoints > this.maxAllowedPointsForInvoice) {
    return `Points entered exceed the outstanding invoice balance.`;
  }
  return '';
}

onPointsInputChange(event: Event): void {
  const input = event.target as HTMLInputElement;
  const rawValue = input.value.trim();

  if (rawValue === '') {
    this.enteredPoints = null;
    return;
  }

  const val = parseFloat(rawValue);
if (val < 0) {
    this.enteredPoints = 0;
    input.value = '0';
  } else {
    this.enteredPoints = val;
  }
}

get hasMoreInvoices(): boolean {
  return this.invoices.length > this.INITIAL_ROW_COUNT;
}

get visibleInvoiceLimit(): number {
  return this.isExpanded ? this.invoices.length : this.INITIAL_ROW_COUNT;
}


}